"use client";

import { useEffect, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { formatDateTime, formatNumber } from "@/lib/format-number";
import { tPlural } from "@/lib/i18n/interpolate";

const RE_DIGITS = /^-?\d+$/;
const MS_THRESHOLD = 1_000_000_000_000;

type RelativeUnit = "second" | "minute" | "hour" | "day" | "month" | "year";

const RELATIVE_UNITS: { limit: number; divisor: number; unit: RelativeUnit }[] =
  [
    { limit: 60_000, divisor: 1000, unit: "second" },
    { limit: 3_600_000, divisor: 60_000, unit: "minute" },
    { limit: 86_400_000, divisor: 3_600_000, unit: "hour" },
    { limit: 2_592_000_000, divisor: 86_400_000, unit: "day" },
    { limit: 31_536_000_000, divisor: 2_592_000_000, unit: "month" },
    { limit: Number.POSITIVE_INFINITY, divisor: 31_536_000_000, unit: "year" },
  ];

type TimestampUnit = "seconds" | "milliseconds";

interface TimestampResult {
  ms: number;
  unit: TimestampUnit;
}

/** The unit a bare number is assumed to be in when the reader has not chosen one. */
export function guessTimestampUnit(value: number): TimestampUnit {
  return Math.abs(value) > MS_THRESHOLD ? "milliseconds" : "seconds";
}

/**
 * Parses a whole-number timestamp. The unit is guessed from the magnitude unless the
 * reader chose one, because a millisecond value from before 9 September 2001 is
 * indistinguishable from a far-future seconds value.
 */
/** 9999-12-31T23:59:59.999Z, the last instant with a four-digit ISO 8601 year. */
const MAX_ISO_MS = 253_402_300_799_999;

export function parseTimestamp(
  input: string,
  chosenUnit: TimestampUnit | null = null
): TimestampResult | null {
  const trimmed = input.trim();
  if (!RE_DIGITS.test(trimmed)) {
    return null;
  }
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isSafeInteger(value)) {
    return null;
  }
  let unit = chosenUnit ?? guessTimestampUnit(value);
  // Unguessed, a pre-2001 millisecond value read as seconds lands past year 9999;
  // milliseconds is then the only reading that gives a real date.
  if (
    chosenUnit === null &&
    unit === "seconds" &&
    Math.abs(value * 1000) > MAX_ISO_MS
  ) {
    unit = "milliseconds";
  }
  const ms = unit === "milliseconds" ? value : value * 1000;
  // Past year 9999 a date no longer fits ISO 8601's four-digit year, and a "timestamp"
  // that large is almost always a millisecond value read as seconds.
  if (Math.abs(ms) > MAX_ISO_MS || Number.isNaN(new Date(ms).getTime())) {
    return null;
  }
  return { ms, unit };
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-border bg-card p-4">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-mono text-foreground text-sm">{value}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TIMESTAMP_CONVERTER_STRINGS = {
  currentTimestamp: "Current Unix timestamp (seconds)",
  copied: "Copied!",
  copy: "Copy",
  loading: "…",
  timestampToDate: "Timestamp to date",
  timestampLabel: "Unix timestamp (seconds or milliseconds)",
  timestampPlaceholder: "1753660800",
  timestampError:
    "Enter a whole number of seconds (10 digits for current dates) or milliseconds (13 digits).",
  detectedUnit: "Detected unit:",
  secondsUnit: "seconds",
  millisecondsUnit: "milliseconds",
  millisecondsNote:
    "(values above 1,000,000,000,000 are treated as milliseconds)",
  utc: "UTC",
  iso8601: "ISO 8601",
  yourLocalTime: "Your local time",
  relative: "Relative",
  dateToTimestamp: "Date to timestamp",
  pickDateTime: "Pick a date and time (your local time)",
  timestampSeconds: "Unix timestamp (seconds)",
  timestampMilliseconds: "Unix timestamp (milliseconds)",
  justNow: "just now",
  ago: "ago",
  in: "in",
  relativeUnits: {
    second: "one: second|other: seconds",
    minute: "one: minute|other: minutes",
    hour: "one: hour|other: hours",
    day: "one: day|other: days",
    month: "one: month|other: months",
    year: "one: year|other: years",
  },
} as const;

function relativeTime(
  ms: number,
  strings: typeof TIMESTAMP_CONVERTER_STRINGS,
  locale: string
): string {
  const diff = Date.now() - ms;
  const absDiff = Math.abs(diff);
  if (absDiff < 5000) {
    return strings.justNow;
  }
  // The last entry's limit is Infinity, so find always matches.
  const match =
    RELATIVE_UNITS.find((u) => absDiff < u.limit) ?? RELATIVE_UNITS[0];
  const amount = Math.floor(absDiff / match.divisor);
  const unit = tPlural(strings.relativeUnits[match.unit], locale, amount);
  const amountText = formatNumber(locale, amount);
  if (diff > 0) {
    return `${amountText} ${unit} ${strings.ago}`;
  }
  return `${strings.in} ${amountText} ${unit}`;
}

export default function TimestampConverterWidget() {
  const strings = useToolUi(TIMESTAMP_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const [tsInput, setTsInput] = useState("");
  const [chosenUnit, setChosenUnit] = useState<TimestampUnit | null>(null);
  const [dateInput, setDateInput] = useState("");
  const [now, setNow] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setNow(Date.now());
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const parsed =
    tsInput.trim().length > 0 ? parseTimestamp(tsInput, chosenUnit) : null;
  const showTsError = tsInput.trim().length > 0 && parsed === null;
  const parsedDate = parsed === null ? null : new Date(parsed.ms);

  const dateMs =
    dateInput.length > 0 ? new Date(dateInput).getTime() : Number.NaN;
  const dateValid = !Number.isNaN(dateMs);
  const nowSeconds = now === null ? 0 : Math.floor(now / 1000);

  function handleCopyNow() {
    if (now === null) {
      return;
    }
    writeToClipboard(String(nowSeconds)).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Current timestamp */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-5">
        <div>
          <p className="text-muted-foreground text-xs">
            {strings.currentTimestamp}
          </p>
          <p className="font-mono font-semibold text-2xl text-foreground">
            {now === null ? strings.loading : nowSeconds}
          </p>
        </div>
        <button
          className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
          onClick={handleCopyNow}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </section>

      {/* Timestamp -> date */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.timestampToDate}
        </h2>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="ts-input"
          >
            {strings.timestampLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="ts-input"
            inputMode="numeric"
            onChange={(e) => {
              setTsInput(e.target.value);
              setChosenUnit(null);
            }}
            placeholder={strings.timestampPlaceholder}
            type="text"
            value={tsInput}
          />
        </div>
        {showTsError && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {strings.timestampError}
          </p>
        )}
        {parsed !== null && parsedDate !== null && (
          <div className="space-y-3">
            <PillGroup<TimestampUnit>
              label={strings.detectedUnit}
              name="ts-unit"
              onChange={setChosenUnit}
              options={[
                { value: "seconds", label: strings.secondsUnit },
                { value: "milliseconds", label: strings.millisecondsUnit },
              ]}
              value={parsed.unit}
            />
            {chosenUnit === null && (
              <p className="text-muted-foreground text-xs">
                {strings.millisecondsNote}
              </p>
            )}
            <div className="space-y-3">
              <ResultRow label={strings.utc} value={parsedDate.toUTCString()} />
              <ResultRow
                label={strings.iso8601}
                value={parsedDate.toISOString()}
              />
              <ResultRow
                label={strings.yourLocalTime}
                value={formatDateTime(locale, parsedDate)}
              />
              <ResultRow
                label={strings.relative}
                value={relativeTime(parsed.ms, strings, locale)}
              />
            </div>
          </div>
        )}
      </section>

      {/* Date -> timestamp */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.dateToTimestamp}
        </h2>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="date-input"
          >
            {strings.pickDateTime}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="date-input"
            onChange={(e) => setDateInput(e.target.value)}
            type="datetime-local"
            value={dateInput}
          />
        </div>
        {dateValid && (
          <div className="space-y-3">
            <ResultRow
              label={strings.timestampSeconds}
              value={String(Math.floor(dateMs / 1000))}
            />
            <ResultRow
              label={strings.timestampMilliseconds}
              value={String(dateMs)}
            />
            <ResultRow
              label={strings.utc}
              value={new Date(dateMs).toUTCString()}
            />
          </div>
        )}
      </section>
    </>
  );
}
